<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { visualToLayout } from 'dashboard/helper/dashboardZoom';
import { useBranding } from 'shared/composables/useBranding';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { parseBoolean } from '@chatwoot/utils';
import { useMediaQuery } from '@vueuse/core';
import SearchAPI from 'dashboard/api/search';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import AceleresNewConversationPanel from 'dashboard/components/AceleresNewConversationPanel.vue';
import UserBasicDetails from './UserBasicDetails.vue';
import ChangeEmailModal from './ChangeEmailModal.vue';
import ProfileAvatarEditor from './ProfileAvatarEditor.vue';
import ChangePassword from './ChangePassword.vue';
import NotificationPreferences from './NotificationPreferences.vue';
import AudioNotifications from './AudioNotifications.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import MfaSettingsCard from './MfaSettingsCard.vue';
import Policy from 'dashboard/components/policy.vue';
import {
  ROLES,
  CONVERSATION_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

const normalizeProfileSearch = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

export default {
  components: {
    AceleresNewConversationPanel,
    Avatar,
    SectionLayout,
    Policy,
    UserBasicDetails,
    ChangeEmailModal,
    ProfileAvatarEditor,
    ChangePassword,
    NotificationPreferences,
    AudioNotifications,
    MfaSettingsCard,
  },
  setup() {
    const { replaceInstallationName } = useBranding();
    const isMobileViewport = useMediaQuery('(max-width: 767px)');

    return {
      replaceInstallationName,
      isMobileViewport,
    };
  },
  data() {
    return {
      avatarFile: '',
      avatarUrl: '',
      avatarEditorFile: null,
      isSavingAvatar: false,
      name: '',
      displayName: '',
      email: '',
      // Os inputs de UserBasicDetails guardam copia local dos props e o watcher
      // so dispara quando o prop MUDA. Numa gravacao que o servidor nao aceita
      // por inteiro (troca de e-mail, que fica pendente de confirmacao) o prop
      // continua igual e o campo ficaria mostrando um valor que nao foi salvo.
      // Trocar a key remonta o formulario com o estado realmente persistido.
      profileFormKey: 0,
      // Trocar o e-mail deixou de ser edicao inline: virou dialogo proprio, com
      // confirmacao do endereco e senha. Quem manda no estado e a tela pai,
      // porque so ela sabe se o servidor aceitou — na recusa o dialogo fica
      // aberto com o motivo, em vez de a pessoa perder o que digitou.
      showEmailModal: false,
      isSavingEmail: false,
      emailModalError: '',
      ultimoErroPerfil: '',
      notificationPermissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      adminPermissions: ['administrator'],
      audioNotificationPermissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      activeSection: 'profile',
      searchQuery: '',
      profilePanelWidth: 497,
      isProfilePanelResizing: false,
      resizeStartX: 0,
      resizeStartWidth: 0,
      selectedDocumentFile: null,
      showDocumentRecipientModal: false,
      documentRecipientSearch: '',
      documentRecipientSearchResults: [],
      documentRecipientSearchTimer: null,
      documentRecipientSearchSequence: 0,
      isSearchingDocumentRecipients: false,
      documentRecipientSearchFailed: false,
      isSendingDocument: false,
      showProfileContactPanel: false,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentUserId: 'getCurrentUserID',
      globalConfig: 'globalConfig/get',
      allConversations: 'getAllConversations',
    }),
    isMfaEnabled() {
      return parseBoolean(window.chatwootConfig?.isMfaEnabled);
    },
    profileSections() {
      return [
        {
          id: 'profile',
          label: 'Perfil',
          subtitle: 'Nome, nome de exibição e e-mail',
          icon: 'i-lucide-user-round',
        },
        {
          id: 'account',
          label: 'Senha',
          subtitle: 'Senha de acesso e autenticação da conta',
          icon: 'i-lucide-lock-keyhole',
        },
        {
          id: 'audio',
          label: 'Alertas de áudio',
          subtitle: 'Sons e combinações dos alertas de áudio',
          icon: 'i-lucide-volume-2',
        },
        {
          id: 'notifications',
          label: this.$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TITLE'),
          subtitle: 'E-mail, push e filtros de notificação',
          icon: 'i-lucide-bell',
        },
      ];
    },
    filteredProfileSections() {
      const query = normalizeProfileSearch(this.searchQuery);
      if (!query) return this.profileSections;
      return this.profileSections.filter(section => {
        const searchableText = normalizeProfileSearch(
          `${section.label} ${section.subtitle}`
        );
        return searchableText.includes(query);
      });
    },
    profileSearchFeedback() {
      const count = this.filteredProfileSections.length;
      if (!count) return 'Nenhuma opção encontrada';
      return `${count} ${count === 1 ? 'resultado encontrado' : 'resultados encontrados'}`;
    },
    activeSectionLabel() {
      return (
        this.profileSections.find(section => section.id === this.activeSection)
          ?.label || ''
      );
    },
    profileAccountId() {
      const match = window.location.pathname.match(/\/accounts\/(\d+)/);
      return match ? Number(match[1]) : null;
    },
    backLabel() {
      return 'Voltar';
    },
    profileEditorTitle() {
      return 'Editar perfil';
    },
    profilePageTitle() {
      return 'Perfil';
    },
    settingsPageTitle() {
      return 'Configurações';
    },
    searchPlaceholder() {
      return 'Pesquisar';
    },
    uploadDocumentLabel() {
      return 'Enviar documento';
    },
    addContactLabel() {
      return 'Adicionar contato';
    },
    documentRecipientModalTitle() {
      return 'Enviar documento';
    },
    documentRecipientSearchPlaceholder() {
      return 'Pesquisar contatos';
    },
    documentRecipientEmptyLabel() {
      return 'Nenhum contato encontrado.';
    },
    documentRecipientLoadingLabel() {
      return 'Buscando contatos...';
    },
    documentRecipientLoadingMoreLabel() {
      return 'Buscando mais contatos...';
    },
    selectedDocumentName() {
      return this.selectedDocumentFile?.name || '';
    },
    documentRecipients() {
      const recipientsById = new Map();
      (this.allConversations || []).forEach(conversation => {
        const sender = conversation?.meta?.sender;
        if (!sender?.id || recipientsById.has(sender.id)) return;
        recipientsById.set(sender.id, {
          id: sender.id,
          name: sender.name || sender.phone_number || sender.email || 'Contato',
          subtitle: sender.phone_number || sender.email || '',
          avatarUrl: sender.thumbnail || sender.avatar_url || '',
          conversationId: conversation.id,
        });
      });

      return Array.from(recipientsById.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    },
    filteredDocumentRecipients() {
      const query = this.documentRecipientSearch.trim().toLowerCase();
      if (!query) {
        return this.documentRecipientSearchResults.length
          ? this.documentRecipientSearchResults
          : this.documentRecipients;
      }

      if (query.length >= 2) return this.documentRecipientSearchResults;

      return this.documentRecipients.filter(recipient =>
        [recipient.name, recipient.subtitle].some(value =>
          String(value || '')
            .toLowerCase()
            .includes(query)
        )
      );
    },
    documentRecipientStatusLabel() {
      if (this.documentRecipientSearchFailed) {
        return 'Não foi possível pesquisar contatos.';
      }
      if (this.documentRecipientSearch.trim().length === 1) {
        return 'Digite pelo menos 2 caracteres para pesquisar.';
      }
      return this.documentRecipientEmptyLabel;
    },
    closeLabel() {
      return 'Fechar';
    },
    clearSearchLabel() {
      return 'Limpar busca';
    },
  },
  watch: {
    documentRecipientSearch(query) {
      this.scheduleDocumentRecipientSearch(query);
    },
  },
  mounted() {
    this.profilePanelWidth = this.readStoredProfilePanelWidth();
    window.addEventListener('keydown', this.onProfileDrawerKeydown);
    this.$nextTick(() => {
      this.$refs.profileDrawerBack?.focus({ preventScroll: true });
    });
    if (this.currentUserId) {
      this.initializeUser();
    }
  },
  beforeUnmount() {
    this.stopProfilePanelResize();
    this.cancelDocumentRecipientSearch();
    window.removeEventListener('keydown', this.onProfileDrawerKeydown);
  },
  methods: {
    initializeUser() {
      this.name = this.currentUser.name;
      this.email = this.currentUser.email;
      this.avatarUrl = this.currentUser.avatar_url;
      this.displayName = this.currentUser.display_name;
    },
    async dispatchUpdate(payload, successMessage, errorMessage, options = {}) {
      let alertMessage = '';
      let ok = false;
      this.ultimoErroPerfil = '';
      try {
        await this.$store.dispatch('updateProfile', payload);
        alertMessage = successMessage;
        ok = true;

        return true; // return the value so that the status can be known
      } catch (error) {
        this.ultimoErroPerfil = parseAPIErrorResponse(error) || errorMessage;
        alertMessage = this.ultimoErroPerfil;

        return false; // return the value so that the status can be known
      } finally {
        // Na recusa da troca de e-mail o motivo aparece dentro do dialogo, ao
        // lado do campo que precisa ser corrigido. Um toast por cima seria a
        // mesma frase duas vezes.
        if (ok || !options.silenciarErro) useAlert(alertMessage);
      }
    },
    async updateProfile(userAttributes) {
      const { name, email, displayName, currentPassword, silenciarErro } =
        userAttributes;
      const hasEmailChanged = this.currentUser.email !== email;

      // O estado da tela so muda DEPOIS de o servidor aceitar. Gravar aqui
      // antes deixava o campo exibindo, em modo leitura, um valor recusado
      // (e-mail ja em uso) como se tivesse sido salvo.
      // `displayName` usa `??`: string vazia significa "apagar o apelido",
      // nao "nao informado" — com `||` o valor antigo voltava ao payload.
      const updatePayload = {
        name: name || this.name,
        email: email || this.email,
        displayName: displayName ?? this.displayName,
        avatar: this.avatarFile,
        // So viaja na troca de e-mail: e a senha do Aceleres, que confirma a
        // identidade porque o e-mail e a credencial de login de la.
        currentPassword,
      };

      const success = await this.dispatchUpdate(
        updatePayload,
        hasEmailChanged
          ? this.$t('PROFILE_SETTINGS.AFTER_EMAIL_CHANGED')
          : this.$t('PROFILE_SETTINGS.UPDATE_SUCCESS'),
        this.$t('RESET_PASSWORD.API.ERROR_MESSAGE'),
        { silenciarErro }
      );

      if (success) {
        this.avatarFile = '';
        this.initializeUser();
      }

      // Remonta o formulario com o estado persistido tanto no sucesso quanto na
      // recusa: sem isso o campo voltava para leitura exibindo o valor que o
      // servidor nao aceitou.
      this.profileFormKey += 1;

      return success;
    },
    openEmailModal() {
      this.emailModalError = '';
      this.showEmailModal = true;
    },
    closeEmailModal() {
      if (this.isSavingEmail) return;
      this.showEmailModal = false;
      this.emailModalError = '';
    },
    async submitEmailChange({ email, currentPassword }) {
      this.isSavingEmail = true;
      this.emailModalError = '';

      const success = await this.updateProfile({
        name: this.name,
        displayName: this.displayName,
        email,
        currentPassword,
        silenciarErro: true,
      });

      this.isSavingEmail = false;
      if (success) {
        this.showEmailModal = false;
      } else {
        this.emailModalError =
          this.ultimoErroPerfil ||
          this.$t('PROFILE_SETTINGS.FORM.EMAIL.CHANGE.PASSWORD_ERROR');
      }
    },
    onProfileAvatarSelected(event) {
      const [file] = Array.from(event.target.files || []);
      event.target.value = '';
      if (!file) return;

      this.avatarEditorFile = file;
    },
    cancelProfileAvatarEdit() {
      this.avatarEditorFile = null;
    },
    async saveProfileAvatar(file) {
      this.isSavingAvatar = true;
      const success = await this.dispatchUpdate(
        {
          name: this.name,
          email: this.email,
          displayName: this.displayName,
          avatar: file,
        },
        this.$t('PROFILE_SETTINGS.UPDATE_SUCCESS'),
        this.$t('PROFILE_SETTINGS.FORM.ERROR')
      );

      if (success) {
        this.avatarEditorFile = null;
        this.avatarFile = '';
        this.initializeUser();
      }
      this.isSavingAvatar = false;
    },
    async deleteProfilePicture() {
      if (this.avatarEditorFile) {
        this.cancelProfileAvatarEdit();
        return;
      }

      try {
        await this.$store.dispatch('deleteAvatar');
        this.avatarUrl = '';
        this.avatarFile = '';
        this.initializeUser();
        useAlert(this.$t('PROFILE_SETTINGS.AVATAR_DELETE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('PROFILE_SETTINGS.AVATAR_DELETE_FAILED'));
      }
    },
    readStoredProfilePanelWidth() {
      const minWidth = 320;
      const maxWidth = 900;
      try {
        const storedWidth = parseInt(
          localStorage.getItem('acl_profile_settings_panel_width') || '',
          10
        );
        if (
          Number.isFinite(storedWidth) &&
          storedWidth >= minWidth &&
          storedWidth <= maxWidth
        ) {
          return storedWidth;
        }
      } catch (error) {
        // Ignore storage errors.
      }
      return 497;
    },
    selectSection(sectionId) {
      this.activeSection = sectionId;
      if (
        ['profile', 'account', 'audio', 'notifications'].includes(sectionId)
      ) {
        // preventScroll: o drawer ainda entra de translateX(100%) dentro do
        // aside com overflow hidden; focar sem ele rolava o aside 290px e a
        // lista de opcoes tremia enquanto a transicao devolvia o scroll.
        this.$nextTick(() => {
          (
            this.$refs.profileDrawerBack || this.$refs.settingsDrawerBack
          )?.focus({ preventScroll: true });
        });
      }
    },
    closeSection() {
      this.activeSection = '';
    },
    onProfileDrawerKeydown(event) {
      if (
        event.key === 'Escape' &&
        ['profile', 'account', 'audio', 'notifications'].includes(
          this.activeSection
        )
      ) {
        this.closeSection();
      }
    },
    clearSearch() {
      this.searchQuery = '';
    },
    onProfilePanelResizeMove(event) {
      const minWidth = 320;
      const maxWidth = 900;
      // Aceleres 2026-08-25: delta visual -> largura em CSS (layout).
      // Ver helper/dashboardZoom.js.
      const delta = visualToLayout(event.clientX - this.resizeStartX);
      const nextWidth = Math.min(
        maxWidth,
        Math.max(minWidth, this.resizeStartWidth + delta)
      );
      this.profilePanelWidth = nextWidth;
    },
    stopProfilePanelResize() {
      if (this.isProfilePanelResizing) {
        try {
          localStorage.setItem(
            'acl_profile_settings_panel_width',
            String(this.profilePanelWidth)
          );
        } catch (error) {
          // Ignore storage errors.
        }
      }
      this.isProfilePanelResizing = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', this.onProfilePanelResizeMove);
      window.removeEventListener('mouseup', this.stopProfilePanelResize);
    },
    startProfilePanelResize(event) {
      this.isProfilePanelResizing = true;
      this.resizeStartX = event.clientX;
      this.resizeStartWidth = this.profilePanelWidth;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('mousemove', this.onProfilePanelResizeMove);
      window.addEventListener('mouseup', this.stopProfilePanelResize);
    },
    resetProfilePanelWidth() {
      this.profilePanelWidth = 497;
      try {
        localStorage.setItem('acl_profile_settings_panel_width', '497');
      } catch (error) {
        // Ignore storage errors.
      }
    },
    openDocumentPicker() {
      this.$refs.profileDocumentInput?.click();
    },
    openContactAction() {
      this.showProfileContactPanel = true;
    },
    closeProfileContactPanel() {
      this.showProfileContactPanel = false;
    },
    handleProfileContactCreated() {
      this.showProfileContactPanel = false;
      useAlert('Contato criado com sucesso.');
    },
    // Esta tela nao hospeda o fluxo de iniciar conversa (ver
    // AceleresStartConversationFlow), entao aqui o telefone ja cadastrado so
    // pode virar aviso -- o que nao pode acontecer e o painel fechar dizendo
    // "contato criado com sucesso" sem nada ter sido criado.
    handleProfileDuplicatePhone() {
      this.showProfileContactPanel = false;
      useAlert('O número informado já está cadastrado nesta conta.');
    },
    async onDocumentSelected(event) {
      const [file] = Array.from(event.target.files || []);
      event.target.value = '';
      if (!file) return;

      this.resetDocumentRecipientSearch();
      this.selectedDocumentFile = file;
      this.showDocumentRecipientModal = true;
      await this.loadInitialDocumentRecipients();
    },
    mapConversationToDocumentRecipient(conversation) {
      const contact = conversation?.contact || conversation?.meta?.sender;
      if (!contact?.id || !conversation?.id) return null;

      return {
        id: contact.id,
        name:
          contact.name || contact.phone_number || contact.email || 'Contato',
        subtitle: contact.phone_number || contact.email || '',
        avatarUrl:
          contact.thumbnail || contact.avatar_url || contact.avatarUrl || '',
        conversationId: conversation.id,
      };
    },
    scheduleDocumentRecipientSearch(query) {
      if (this.documentRecipientSearchTimer) {
        clearTimeout(this.documentRecipientSearchTimer);
        this.documentRecipientSearchTimer = null;
      }

      const normalizedQuery = String(query || '').trim();
      this.documentRecipientSearchSequence += 1;
      const sequence = this.documentRecipientSearchSequence;
      this.documentRecipientSearchResults = [];
      this.documentRecipientSearchFailed = false;

      if (!normalizedQuery) {
        if (this.showDocumentRecipientModal) {
          this.loadInitialDocumentRecipients(sequence);
        } else {
          this.isSearchingDocumentRecipients = false;
        }
        return;
      }

      if (normalizedQuery.length < 2) {
        this.isSearchingDocumentRecipients = false;
        return;
      }

      this.isSearchingDocumentRecipients = true;
      this.documentRecipientSearchTimer = setTimeout(() => {
        this.documentRecipientSearchTimer = null;
        this.searchDocumentRecipients(normalizedQuery, sequence);
      }, 300);
    },
    async loadInitialDocumentRecipients(sequence = null) {
      const requestSequence =
        sequence ?? this.documentRecipientSearchSequence + 1;
      this.documentRecipientSearchSequence = requestSequence;
      this.isSearchingDocumentRecipients = true;
      this.documentRecipientSearchFailed = false;

      try {
        const { data } = await SearchAPI.conversations({ q: '', page: 1 });
        if (requestSequence !== this.documentRecipientSearchSequence) return;

        const recipientsById = new Map(
          this.documentRecipients.map(recipient => [recipient.id, recipient])
        );
        const conversations = data?.payload?.conversations || [];
        conversations.forEach(conversation => {
          const recipient =
            this.mapConversationToDocumentRecipient(conversation);
          if (recipient && !recipientsById.has(recipient.id)) {
            recipientsById.set(recipient.id, recipient);
          }
        });

        this.documentRecipientSearchResults = Array.from(
          recipientsById.values()
        ).sort((a, b) => a.name.localeCompare(b.name));
      } catch (error) {
        if (requestSequence === this.documentRecipientSearchSequence) {
          this.documentRecipientSearchFailed = true;
        }
      } finally {
        if (requestSequence === this.documentRecipientSearchSequence) {
          this.isSearchingDocumentRecipients = false;
        }
      }
    },
    async searchDocumentRecipients(query, sequence) {
      const recipientsById = new Map();
      let page = 1;
      const pageSize = 15;

      try {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { data } = await SearchAPI.conversations({ q: query, page });
          if (sequence !== this.documentRecipientSearchSequence) return;

          const conversations = data?.payload?.conversations || [];
          conversations.forEach(conversation => {
            const recipient =
              this.mapConversationToDocumentRecipient(conversation);
            if (recipient && !recipientsById.has(recipient.id)) {
              recipientsById.set(recipient.id, recipient);
            }
          });

          this.documentRecipientSearchResults = Array.from(
            recipientsById.values()
          ).sort((a, b) => a.name.localeCompare(b.name));

          if (conversations.length < pageSize) break;
          page += 1;
        }
      } catch (error) {
        if (sequence === this.documentRecipientSearchSequence) {
          this.documentRecipientSearchFailed = true;
          this.documentRecipientSearchResults = [];
        }
      } finally {
        if (sequence === this.documentRecipientSearchSequence) {
          this.isSearchingDocumentRecipients = false;
        }
      }
    },
    cancelDocumentRecipientSearch() {
      if (this.documentRecipientSearchTimer) {
        clearTimeout(this.documentRecipientSearchTimer);
        this.documentRecipientSearchTimer = null;
      }
      this.documentRecipientSearchSequence += 1;
      this.isSearchingDocumentRecipients = false;
    },
    resetDocumentRecipientSearch() {
      this.cancelDocumentRecipientSearch();
      this.documentRecipientSearch = '';
      this.documentRecipientSearchResults = [];
      this.documentRecipientSearchFailed = false;
    },
    closeDocumentRecipientModal() {
      if (this.isSendingDocument) return;
      this.showDocumentRecipientModal = false;
      this.selectedDocumentFile = null;
      this.resetDocumentRecipientSearch();
    },
    async sendDocumentToRecipient(recipient) {
      if (!this.selectedDocumentFile || !recipient?.conversationId) return;

      this.isSendingDocument = true;
      try {
        await this.$store.dispatch('createPendingMessageAndSend', {
          conversationId: recipient.conversationId,
          message: '',
          file: this.selectedDocumentFile,
          files: [this.selectedDocumentFile],
          private: false,
        });
        useAlert('Documento enviado.');
        this.showDocumentRecipientModal = false;
        this.selectedDocumentFile = null;
        this.resetDocumentRecipientSearch();
      } catch (error) {
        useAlert('Não foi possível enviar o documento.');
      } finally {
        this.isSendingDocument = false;
      }
    },
  },
};
</script>

<template>
  <section
    class="profile-settings-workspace acl-profile-settings-workspace max-md:!relative max-md:!h-full"
  >
    <aside
      class="profile-settings-nav max-md:!h-auto max-md:!min-h-0 max-md:!flex-1 max-md:!border-r-0"
      :style="{ width: profilePanelWidth + 'px' }"
    >
      <div
        class="profile-settings-nav__fixed max-md:!px-4 max-md:!pt-3 max-md:!pb-2"
      >
        <div
          class="profile-settings-nav__title-row acl-mobile-account-menu-host max-md:!mb-3 max-md:!items-center"
        >
          <h1
            class="profile-settings-nav__title max-md:!text-xl max-md:!leading-tight"
          >
            {{
              isMobileViewport
                ? settingsPageTitle
                : name || $t('PROFILE_SETTINGS.TITLE')
            }}
          </h1>
        </div>
        <div class="profile-settings-search max-md:hidden">
          <svg
            class="profile-settings-search__icon"
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            v-model="searchQuery"
            class="profile-settings-search__input"
            type="text"
            :placeholder="searchPlaceholder"
          />
          <button
            v-if="searchQuery.length > 0"
            type="button"
            class="profile-settings-search__clear"
            :aria-label="clearSearchLabel"
            @click="clearSearch"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      <div
        id="profile-settings-mobile-navigation"
        class="profile-settings-nav__scroll max-md:!px-3 max-md:!pt-2 max-md:!pb-3"
        :role="isMobileViewport ? 'navigation' : undefined"
        :aria-label="isMobileViewport ? settingsPageTitle : undefined"
      >
        <div
          v-if="searchQuery.trim()"
          class="profile-settings-search-feedback"
          :class="{
            'profile-settings-search-feedback--empty':
              !filteredProfileSections.length,
          }"
          aria-live="polite"
        >
          {{ profileSearchFeedback }}
        </div>

        <div
          v-if="!isMobileViewport && !searchQuery.trim()"
          class="profile-settings-avatar"
        >
          <Avatar :src="avatarUrl" :name="name" :size="128" rounded-full />
        </div>

        <button
          v-for="section in filteredProfileSections"
          :key="section.id"
          type="button"
          class="profile-settings-nav__item max-md:!mb-0 max-md:!h-auto max-md:!min-h-14 max-md:!rounded-none max-md:!border-x-0 max-md:!border-t-0 max-md:!border-b max-md:!border-n-weak max-md:!px-4 max-md:!py-2 max-md:!shadow-none"
          :class="{
            'profile-settings-nav__item--active': activeSection === section.id,
          }"
          @click="selectSection(section.id)"
        >
          <span
            class="profile-settings-nav__item-icon max-md:!size-5"
            :class="section.icon"
          />
          <span class="profile-settings-nav__item-copy">
            <span class="profile-settings-nav__item-label max-md:!text-base">
              {{ section.label }}
            </span>
            <span class="profile-settings-nav__item-subtitle max-md:hidden">
              {{ section.subtitle }}
            </span>
          </span>
          <span
            class="i-lucide-chevron-right hidden size-5 flex-shrink-0 text-n-slate-10 max-md:block"
            aria-hidden="true"
          />
        </button>
      </div>

      <Transition
        :css="!isMobileViewport"
        mode="out-in"
        enter-active-class="profile-settings-drawer-enter-active"
        enter-from-class="profile-settings-drawer-from-right"
        enter-to-class="profile-settings-drawer-to"
        leave-active-class="profile-settings-drawer-leave-active"
        leave-from-class="profile-settings-drawer-to"
        leave-to-class="profile-settings-drawer-leave-to-right"
      >
        <section
          v-if="activeSection === 'profile'"
          key="profile"
          class="profile-settings-detail profile-settings-detail--drawer"
          data-profile-section="profile"
          role="dialog"
          aria-modal="true"
          aria-labelledby="profile-settings-drawer-title"
        >
          <header
            class="profile-settings-detail__header max-md:!min-h-14 max-md:!px-3"
          >
            <button
              ref="profileDrawerBack"
              type="button"
              class="profile-settings-detail__back"
              :aria-label="backLabel"
              @click="closeSection"
            >
              <i class="i-lucide-arrow-left" />
            </button>
            <h2 id="profile-settings-drawer-title">
              {{ isMobileViewport ? profilePageTitle : profileEditorTitle }}
            </h2>
          </header>

          <div
            class="profile-settings-detail__body max-md:!px-4 max-md:!pb-[calc(1rem+env(safe-area-inset-bottom))]"
          >
            <SectionLayout title="" description="" class="!pt-0">
              <div class="flex flex-col gap-6">
                <ProfileAvatarEditor
                  v-if="avatarEditorFile"
                  :file="avatarEditorFile"
                  :name="name"
                  :saving="isSavingAvatar"
                  @cancel="cancelProfileAvatarEdit"
                  @save="saveProfileAvatar"
                />
                <div v-else class="profile-settings-profile-avatar">
                  <Avatar
                    :src="avatarUrl"
                    :name="name"
                    :size="isMobileViewport ? 96 : 128"
                    rounded-full
                  />
                  <input
                    ref="profileAvatarInput"
                    class="profile-settings-avatar-input"
                    type="file"
                    accept="image/*"
                    @change="onProfileAvatarSelected"
                  />
                  <div class="mt-4 flex w-full flex-wrap justify-center gap-2">
                    <button
                      type="button"
                      class="inline-flex items-center gap-2 rounded-full border border-n-strong bg-n-solid-1 px-4 py-2 text-sm font-semibold text-n-brand shadow-sm transition-colors duration-150 hover:border-n-brand hover:bg-n-brand/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand/30"
                      @click="$refs.profileAvatarInput?.click()"
                    >
                      <i class="i-lucide-camera size-4" aria-hidden="true" />
                      <span>
                        {{ $t('PROFILE_SETTINGS.FORM.UPDATE_IMAGE') }}
                      </span>
                    </button>
                    <button
                      v-if="avatarUrl"
                      type="button"
                      class="inline-flex items-center gap-2 rounded-full border border-n-strong bg-n-solid-1 px-4 py-2 text-sm font-semibold text-n-ruby-11 shadow-sm transition-colors duration-150 hover:border-n-ruby-7 hover:bg-n-ruby-3/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-ruby-7/30"
                      @click="deleteProfilePicture"
                    >
                      <i class="i-lucide-trash-2 size-4" aria-hidden="true" />
                      <span>
                        {{ $t('PROFILE_SETTINGS.FORM.REMOVE_IMAGE') }}
                      </span>
                    </button>
                  </div>
                </div>
                <div class="pt-2">
                  <UserBasicDetails
                    :key="profileFormKey"
                    :name="name"
                    :display-name="displayName"
                    :email="email"
                    :email-enabled="!globalConfig.disableUserProfileUpdate"
                    @update-user="updateProfile"
                    @request-email-change="openEmailModal"
                  />
                </div>
              </div>
            </SectionLayout>
          </div>
        </section>

        <section
          v-else-if="
            ['account', 'audio', 'notifications'].includes(activeSection)
          "
          key="settings"
          class="profile-settings-detail profile-settings-detail--drawer"
          :data-profile-section="activeSection"
          role="dialog"
          aria-modal="true"
          aria-labelledby="profile-settings-section-drawer-title"
        >
          <header
            class="profile-settings-detail__header max-md:!min-h-14 max-md:!px-3"
          >
            <button
              ref="settingsDrawerBack"
              type="button"
              class="profile-settings-detail__back"
              :aria-label="backLabel"
              @click="closeSection"
            >
              <i class="i-lucide-arrow-left" />
            </button>
            <h2 id="profile-settings-section-drawer-title">
              {{ activeSectionLabel }}
            </h2>
          </header>

          <div
            class="profile-settings-detail__body max-md:!px-4 max-md:!pb-[calc(1rem+env(safe-area-inset-bottom))]"
          >
            <div
              v-if="activeSection === 'account'"
              class="profile-settings-stack"
            >
              <SectionLayout
                v-if="!globalConfig.disableUserProfileUpdate"
                title=""
                description=""
                class="profile-settings-primary-section--password !pt-0"
              >
                <ChangePassword />
              </SectionLayout>
              <SectionLayout
                v-if="isMfaEnabled"
                with-border
                :title="$t('PROFILE_SETTINGS.FORM.SECURITY_SECTION.TITLE')"
                :description="$t('PROFILE_SETTINGS.FORM.SECURITY_SECTION.NOTE')"
              >
                <MfaSettingsCard />
              </SectionLayout>
            </div>

            <Policy
              v-else-if="activeSection === 'audio'"
              key="profile-audio-drawer"
              :permissions="audioNotificationPermissions"
            >
              <SectionLayout
                title=""
                :description="
                  $t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.NOTE')
                "
                class="profile-settings-primary-section--audio !pt-0"
              >
                <AudioNotifications />
              </SectionLayout>
            </Policy>

            <Policy
              v-else-if="activeSection === 'notifications'"
              key="profile-notifications-drawer"
              :permissions="notificationPermissions"
            >
              <SectionLayout title="" description="" class="!pt-0">
                <NotificationPreferences />
              </SectionLayout>
            </Policy>
          </div>
        </section>
      </Transition>

      <div
        class="profile-settings-resize-handle"
        :class="{
          'profile-settings-resize-handle--active': isProfilePanelResizing,
        }"
        @mousedown.prevent="startProfilePanelResize"
        @dblclick="resetProfilePanelWidth"
      />
    </aside>

    <main
      id="profile-settings-stage"
      class="profile-settings-stage max-md:!hidden"
    >
      <div
        class="profile-settings-stage__actions max-md:!w-full max-md:!flex-nowrap max-md:!gap-2 max-md:!px-3 max-md:!py-2"
      >
        <input
          ref="profileDocumentInput"
          class="profile-settings-file-input"
          type="file"
          @change="onDocumentSelected"
        />
        <button
          type="button"
          class="profile-settings-action max-md:!min-h-[4.5rem] max-md:!min-w-0 max-md:!flex-1 max-md:!gap-1 max-md:!rounded-xl max-md:!border max-md:!border-n-weak max-md:!bg-n-solid-1 max-md:!px-2 max-md:!text-xs"
          @click="openDocumentPicker"
        >
          <span
            class="profile-settings-action__icon-wrap max-md:!size-8 max-md:!bg-n-alpha-2"
          >
            <i
              class="i-lucide-file-up profile-settings-action__icon max-md:!size-5"
            />
          </span>
          <span>{{ uploadDocumentLabel }}</span>
        </button>
        <button
          type="button"
          class="profile-settings-action max-md:!min-h-[4.5rem] max-md:!min-w-0 max-md:!flex-1 max-md:!gap-1 max-md:!rounded-xl max-md:!border max-md:!border-n-weak max-md:!bg-n-solid-1 max-md:!px-2 max-md:!text-xs"
          @click="openContactAction"
        >
          <span
            class="profile-settings-action__icon-wrap max-md:!size-8 max-md:!bg-n-alpha-2"
          >
            <i
              class="i-lucide-user-plus profile-settings-action__icon max-md:!size-5"
            />
          </span>
          <span>{{ addContactLabel }}</span>
        </button>
      </div>

      <AceleresNewConversationPanel
        v-if="showProfileContactPanel && profileAccountId"
        class="profile-settings-contact-panel"
        :show="showProfileContactPanel"
        :account-id="profileAccountId"
        teleport-target="#profile-settings-stage"
        initial-view="new-contact"
        slide-direction="right"
        panel-variant="contact-details"
        @close="closeProfileContactPanel"
        @open-contact-chat="handleProfileContactCreated"
        @open-existing-phone="handleProfileDuplicatePhone"
      />

      <Teleport to="body">
        <div
          v-if="showDocumentRecipientModal"
          class="profile-settings-document-modal-backdrop max-md:!items-end max-md:!p-0"
          @click.self="closeDocumentRecipientModal"
        >
          <section
            class="profile-settings-document-modal max-md:!h-[min(42rem,calc(100dvh-1rem))] max-md:!rounded-t-2xl max-md:!rounded-b-none"
            role="dialog"
            aria-modal="true"
          >
            <header class="profile-settings-document-modal__header">
              <div class="profile-settings-document-modal__heading">
                <h2>{{ documentRecipientModalTitle }}</h2>
                <p v-if="selectedDocumentName">{{ selectedDocumentName }}</p>
              </div>
              <button
                type="button"
                class="profile-settings-document-modal__close"
                :aria-label="closeLabel"
                @click="closeDocumentRecipientModal"
              >
                <i class="i-lucide-x" />
              </button>
            </header>

            <div class="profile-settings-document-modal__search">
              <i class="i-lucide-search" />
              <input
                v-model="documentRecipientSearch"
                type="text"
                :placeholder="documentRecipientSearchPlaceholder"
              />
            </div>

            <div class="profile-settings-document-modal__contacts">
              <p
                v-if="
                  isSearchingDocumentRecipients &&
                  !filteredDocumentRecipients.length
                "
                class="profile-settings-document-modal__empty"
                aria-live="polite"
              >
                {{ documentRecipientLoadingLabel }}
              </p>
              <button
                v-for="recipient in filteredDocumentRecipients"
                :key="recipient.id"
                type="button"
                class="profile-settings-document-contact"
                :disabled="isSendingDocument"
                @click="sendDocumentToRecipient(recipient)"
              >
                <Avatar
                  :src="recipient.avatarUrl"
                  :name="recipient.name"
                  :size="40"
                  rounded-full
                />
                <span class="profile-settings-document-contact__copy">
                  <span>{{ recipient.name }}</span>
                  <small>{{ recipient.subtitle }}</small>
                </span>
              </button>
              <p
                v-if="
                  isSearchingDocumentRecipients &&
                  filteredDocumentRecipients.length
                "
                class="profile-settings-document-modal__empty"
                aria-live="polite"
              >
                {{ documentRecipientLoadingMoreLabel }}
              </p>
              <p
                v-if="
                  !isSearchingDocumentRecipients &&
                  !filteredDocumentRecipients.length
                "
                class="profile-settings-document-modal__empty"
                aria-live="polite"
              >
                {{ documentRecipientStatusLabel }}
              </p>
            </div>
          </section>
        </div>
      </Teleport>
    </main>

    <!-- Fora das secoes de proposito: elas sao v-if/v-else-if entre si, e o
         dialogo precisa existir estando o usuario em qualquer uma delas. -->
    <ChangeEmailModal
      v-if="showEmailModal"
      :current-email="email"
      :saving="isSavingEmail"
      :server-error="emailModalError"
      @close="closeEmailModal"
      @submit="submitEmailChange"
    />
  </section>
</template>

<style scoped>
.profile-settings-workspace {
  display: flex;
  flex: 1;
  width: 100%;
  min-height: inherit;
  margin: 0;
  padding: 0;
  padding-bottom: 0;
  overflow: hidden;
  background: #f6f6f6;
}

.profile-settings-nav {
  position: relative;
  isolation: isolate;
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  min-width: 320px;
  max-width: 900px;
  min-height: inherit;
  overflow: hidden;
  background: #ffffff;
  border-right: 1px solid #e5e7eb;
}

.profile-settings-nav__fixed {
  flex-shrink: 0;
  padding: 18px 14px 8px;
  background: #ffffff;
}

.profile-settings-nav__title {
  min-width: 0;
  margin: 0;
  flex: 1;
  color: #111827;
  font-size: 1.525rem;
  font-weight: 500;
  line-height: 1.3;
  overflow-wrap: anywhere;
  white-space: normal;
}

.profile-settings-nav__title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 14px;
}

.profile-settings-search {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 14px !important;
  border: none;
  border-radius: 50px;
  background: rgba(194, 189, 184, 0.15);
  box-shadow: inset 0 0 0 0 transparent;
  transition: box-shadow 0.12s;
}

.profile-settings-search:hover {
  box-shadow: inset 0 0 0 1px #c4c4c4;
}

.profile-settings-search:focus-within {
  box-shadow: inset 0 0 0 1.5px #1b8755;
}

.profile-settings-search__icon {
  flex-shrink: 0;
  color: #8a8a8a;
}

.profile-settings-search__input {
  flex: 1;
  min-width: 0;
  height: 40px;
  margin: 0;
  padding: 0;
  border: none !important;
  background: transparent !important;
  color: #1f2937;
  font-size: 13px;
  line-height: 40px;
  outline: none !important;
  box-shadow: none !important;
}

.profile-settings-search__input::placeholder {
  color: #8a8a8a;
}

.profile-settings-search__input:focus,
.profile-settings-search__input:focus-visible,
.profile-settings-search__input:focus-within {
  border: none !important;
  background: transparent !important;
  outline: none !important;
  box-shadow: none !important;
}

.profile-settings-search__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 9999px;
  background: transparent;
  color: #8a8a8a;
  cursor: pointer;
  transition:
    background-color 0.12s,
    color 0.12s;
}

.profile-settings-search__clear:hover {
  background: rgba(0, 0, 0, 0.06);
  color: #4b5563;
}

.profile-settings-nav__scroll {
  min-height: 0;
  overflow-y: auto;
  padding: 18px 14px 24px;
  scrollbar-width: thin;
  scrollbar-color: #c7cdd4 transparent;
}

.profile-settings-nav__scroll::-webkit-scrollbar {
  width: 7px;
}

.profile-settings-nav__scroll::-webkit-scrollbar-track {
  background: transparent;
}

.profile-settings-nav__scroll::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: #c7cdd4;
}

.profile-settings-search-feedback {
  margin: 0 4px 12px;
  color: #475569;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.4;
}

.profile-settings-search-feedback--empty {
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
  color: #64748b;
  text-align: center;
}

.profile-settings-avatar {
  display: flex;
  justify-content: center;
  padding: 8px 6px 22px;
}

.profile-settings-profile-avatar {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 8px 0 24px;
}

.profile-settings-avatar-input {
  display: none;
}

.profile-settings-nav__item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 68px;
  height: 68px;
  margin-bottom: 15px;
  padding: 0 30px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: #ffffff;
  color: #111827;
  text-align: left;
  transition:
    background-color 0.12s,
    color 0.12s;
}

.profile-settings-nav__item:hover,
.profile-settings-nav__item--active {
  background: #f7f5f3 !important;
  border-color: transparent;
}

.profile-settings-nav__item-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  color: #6b7280;
  filter: drop-shadow(0 0 0 currentColor);
}

.profile-settings-nav__item-copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  justify-content: center;
}

.profile-settings-nav__item-label {
  overflow: hidden;
  color: #111827;
  font-size: 19px;
  font-weight: 500;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-settings-nav__item-subtitle {
  overflow: hidden;
  margin-top: 3px;
  color: #6b7280;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-settings-resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  width: 4px;
  cursor: col-resize;
  background: transparent;
  transition: background-color 0.12s;
}

.profile-settings-resize-handle:hover,
.profile-settings-resize-handle--active {
  background: rgba(100, 116, 139, 0.35);
}

.profile-settings-stage {
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
  height: 100%;
  min-height: inherit;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #f7f5f3;
}

.profile-settings-stage__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 14px;
  padding: 24px;
}

:global(.profile-settings-stage .acl-new-conv-drawer__content) {
  width: min(430px, 100%);
  box-shadow: -18px 0 40px rgba(15, 23, 42, 0.16);
}

:global(
    .profile-settings-stage .acl-new-conv-drawer__content--contact-details
  ) {
  width: min(100%, 990px);
  right: 0;
  left: auto;
  box-sizing: border-box;
  border-left: 1px solid rgb(var(--n-weak));
  box-shadow: none;
}

:global(.profile-settings-stage .acl-newconv-slide-from) {
  transform: translateX(100%);
}

:global(.profile-settings-stage .acl-newconv-slide-leave-to) {
  transform: translateX(100%);
}

.profile-settings-file-input {
  display: none;
}

.profile-settings-action {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 120px;
  min-height: 92px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #111827;
  font-size: 0.9375rem;
  font-weight: 600;
  box-shadow: none;
}

.profile-settings-action__icon-wrap {
  display: inline-flex;
  width: 70px;
  height: 52px;
  align-items: center;
  justify-content: center;
  border-radius: 50px;
  background: rgba(194, 189, 184, 0.15);
}

.profile-settings-action__icon {
  width: 25px;
  height: 25px;
  color: #000000;
}

.profile-settings-document-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.38);
}

.profile-settings-document-modal {
  display: flex;
  width: min(560px, 100%);
  height: min(640px, calc(100vh - 48px));
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.24);
}

.profile-settings-document-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px 14px;
  border-bottom: 1px solid #eef0f2;
}

.profile-settings-document-modal__heading h2 {
  margin: 0;
  color: #111827;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.25;
}

.profile-settings-document-modal__heading p {
  margin: 6px 0 0;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.35;
  word-break: break-word;
}

.profile-settings-document-modal__close {
  display: inline-flex;
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
}

.profile-settings-document-modal__search {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 16px 22px 12px;
  padding: 0 14px;
  border-radius: 50px;
  background: rgba(194, 189, 184, 0.15);
  color: #8a8a8a;
}

.profile-settings-document-modal__search input {
  flex: 1;
  min-width: 0;
  height: 40px;
  border: 0;
  background: transparent;
  color: #111827;
  font-size: 14px;
  outline: none;
}

.profile-settings-document-modal__contacts {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 12px 14px;
}

.profile-settings-document-contact {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 12px;
  padding: 10px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  text-align: left;
  transition: background-color 0.12s;
}

.profile-settings-document-contact:hover:not(:disabled) {
  background: #f7f5f3;
}

.profile-settings-document-contact:disabled {
  cursor: wait;
  opacity: 0.65;
}

.profile-settings-document-contact__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.profile-settings-document-contact__copy span {
  overflow: hidden;
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-settings-document-contact__copy small {
  overflow: hidden;
  color: #6b7280;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-settings-document-modal__empty {
  margin: 24px 0;
  color: #6b7280;
  font-size: 14px;
  text-align: center;
}

.profile-settings-detail {
  position: absolute;
  top: 24px;
  bottom: 24px;
  left: 24px;
  z-index: 10;
  display: flex;
  width: min(760px, calc(100% - 48px));
  min-width: 0;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.12);
}

.profile-settings-detail--drawer {
  position: absolute;
  inset: 0;
  z-index: 30;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  margin: 0;
  box-sizing: border-box;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

.profile-settings-drawer-enter-active,
.profile-settings-drawer-leave-active {
  transition:
    transform 380ms cubic-bezier(0.22, 0.61, 0.36, 1),
    opacity 260ms ease;
  will-change: transform, opacity;
}

.profile-settings-drawer-from-right,
.profile-settings-drawer-leave-to-right {
  transform: translate3d(100%, 0, 0);
  opacity: 0;
}

.profile-settings-drawer-to {
  transform: translate3d(0, 0, 0);
  opacity: 1;
}

.profile-settings-detail__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
  min-height: 64px;
  padding: 0 22px;
}

.profile-settings-detail--drawer .profile-settings-detail__header {
  justify-content: flex-start;
}

.profile-settings-detail__header h2 {
  margin: 0;
  color: #111827;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.3;
}

.profile-settings-detail__back {
  display: inline-flex;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #111827;
  font-size: 20px;
  transition: background-color 0.16s ease;
}

.profile-settings-detail__back:hover,
.profile-settings-detail__back:focus-visible {
  background: #f3f4f6;
}

.profile-settings-detail__close {
  display: inline-flex;
  width: 44px;
  height: 44px;
  min-width: 44px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
  font-size: 20px;
}

.profile-settings-detail__body {
  min-height: 0;
  overflow-y: auto;
  padding: 0 24px 24px;
  scrollbar-width: thin;
  scrollbar-color: #c7cdd4 transparent;
}

.profile-settings-detail__body::-webkit-scrollbar {
  width: 7px;
}

.profile-settings-detail__body::-webkit-scrollbar-track {
  background: transparent;
}

.profile-settings-detail__body::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: #c7cdd4;
}

.profile-settings-stack {
  display: flex;
  flex-direction: column;
}

:global(.dark) .profile-settings-workspace,
:global(.dark) .profile-settings-stage {
  background: #111315;
}

:global(.dark) .profile-settings-nav,
:global(.dark) .profile-settings-nav__fixed,
:global(.dark) .profile-settings-detail,
:global(.dark) .profile-settings-action {
  background: #181a1d;
  border-color: #2f3338;
}

:global(.dark) .profile-settings-search,
:global(.dark) .profile-settings-nav__item:hover,
:global(.dark) .profile-settings-nav__item--active {
  background: #22252a;
  border-color: #3a3f46;
}

:global(.dark) .profile-settings-search {
  background: rgba(255, 255, 255, 0.04);
}

:global(.dark) .profile-settings-search:hover {
  box-shadow: inset 0 0 0 1px #3f3f46;
}

:global(.dark) .profile-settings-nav__title,
:global(.dark) .profile-settings-nav__item,
:global(.dark) .profile-settings-nav__item-label,
:global(.dark) .profile-settings-action,
:global(.dark) .profile-settings-detail__header h2 {
  color: #f3f4f6;
}

:global(.dark) .profile-settings-detail__back {
  color: #f3f4f6;
}

:global(.dark) .profile-settings-detail__back:hover,
:global(.dark) .profile-settings-detail__back:focus-visible {
  background: #2a2f35;
}

:global(.dark) .profile-settings-nav__item-icon,
:global(.dark) .profile-settings-nav__item-subtitle {
  color: #a1a1aa;
}

:global(.dark) .profile-settings-search__icon,
:global(.dark) .profile-settings-search__input::placeholder,
:global(.dark) .profile-settings-search__clear {
  color: #a1a1aa;
}

:global(.dark) .profile-settings-search__input {
  color: #e5e5e5;
}

:global(.dark) .profile-settings-search__clear:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #e5e5e5;
}

@media (max-width: 900px) {
  .profile-settings-workspace {
    min-height: inherit;
    flex-direction: column;
    overflow: hidden;
  }

  .profile-settings-nav {
    width: 100% !important;
    max-width: none;
    min-width: 0;
    height: 50%;
    min-height: 0;
    max-height: none;
  }

  .profile-settings-resize-handle {
    display: none;
  }

  .profile-settings-stage {
    flex: 1;
    min-height: 0;
  }

  .profile-settings-detail--drawer {
    position: absolute;
    width: 100%;
    margin: 0;
  }
}
</style>

<style>
.acl-profile-settings-workspace {
  display: flex !important;
  flex: 1 1 auto !important;
  width: 100% !important;
  min-height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  padding-bottom: 0 !important;
  overflow: hidden !important;
  background: #f6f6f6 !important;
}

@media (max-width: 900px) {
  .acl-profile-settings-workspace {
    min-height: 100% !important;
    flex-direction: column !important;
    overflow: hidden !important;
  }
}
</style>
